/**
 * Which sessions a person sees in their current space.
 *
 * The session counterpart of lib/itemSpaceScope.ts, reading the same scope
 * (resolveItemSpaceScope / useItemSpaceScope). One rule for the Session List
 * wherever it is mounted — /prioritization/sessions and the Mandate shell's
 * /mandate/sessions — so the two addresses list the same sessions for the same
 * space. Until 2026-11-02 the rule lived only in the Mandate copy of the list
 * (SessionsList.tsx, removed), and the main list showed every session.
 *
 *   Mandate space  → only sessions with originModule = 'mandate'. Full isolation.
 *   Personal space → the sessions you created or facilitate; never mandate ones.
 *   Team / org     → the sessions of this space and those of no space (spaceId
 *                    null, workspace-wide); another space's sessions are hidden,
 *                    as its items are. Never mandate ones.
 *
 * Canon 00 (platform architecture) "Space": a space gates item and session
 * visibility. Until 2026-11-02 a team space listed every workspace session, so
 * switching into one changed nothing on this page.
 *
 * No current space, or spaces not loaded yet → nothing is hidden, so the page
 * does not flash empty while bootstrap is in flight.
 */
import type { ItemSpaceScope, SpaceScopeOptions } from '../itemSpaceScope';

/** The slice of a session this rule reads. */
export interface SpaceScopedSession {
  originModule?: string | null;
  /** The space the session was created in (migration 492); null = workspace-wide. */
  spaceId?: string | null;
  createdBy?: string | null;
  facilitatorId?: string | null;
}

export function isSessionInSpaceScope(
  session: SpaceScopedSession,
  scope: ItemSpaceScope | null,
  userId: string | null | undefined,
  options: SpaceScopeOptions = {},
): boolean {
  if (!scope) return true;
  if (scope.isMandate) return session.originModule === 'mandate';
  if (session.originModule === 'mandate') return false;
  if (options.showAllSpaces) return true;
  if (scope.spaceType === 'personal') {
    if (!userId) return false;
    return session.createdBy === userId || session.facilitatorId === userId;
  }
  return session.spaceId == null || session.spaceId === scope.spaceId;
}

/**
 * The sessions visible in the scope. With no scope the input array itself
 * comes back, so a memo downstream keeps its identity.
 */
export function scopeSessionsToSpace<T extends SpaceScopedSession>(
  sessions: T[],
  scope: ItemSpaceScope | null,
  userId: string | null | undefined,
  options: SpaceScopeOptions = {},
): T[] {
  if (!scope) return sessions;
  return sessions.filter((s) => isSessionInSpaceScope(s, scope, userId, options));
}

/**
 * The line above the list that says which space it is narrowed to, and the
 * switch that widens it — the Items list's words, said of sessions. null for no
 * scope and for a mandate space (its isolation is the point).
 */
export function describeSessionSpaceScope(
  scope: ItemSpaceScope | null,
  options: SpaceScopeOptions = {},
): { label: string; toggleLabel: string } | null {
  if (!scope || scope.isMandate) return null;
  if (options.showAllSpaces) {
    return { label: 'Showing sessions from all spaces', toggleLabel: `Back to "${scope.spaceName}"` };
  }
  if (scope.spaceType === 'personal') {
    return { label: `Viewing: "${scope.spaceName}" (sessions you created or facilitate)`, toggleLabel: 'Show all sessions' };
  }
  return { label: `Viewing: "${scope.spaceName}"`, toggleLabel: 'Show all spaces' };
}
